import type { ReactNode } from 'react'
import { ruleHint, ruleTitle } from '../lib/activeRules.ts'
import type { Finding } from '../lib/api.ts'
import { reasonFor } from '../lib/verificationItems.ts'
import { CHECK_LABEL, label } from '../lib/vocabulary.ts'
import { ReasonIcon } from './ReasonIcon.tsx'

/**
 * Under a row: the operational rules' reminders about the same thing (#549).
 *
 * A reminder asks nothing, so it is a line beneath the thing it is about
 * rather than a row of the rules section naming that thing in text. The same
 * "↳" the Correção view hangs under a row, because it is the same relation:
 * something said about the line above.
 *
 * Laid out on the row's own columns -- the pin's, the values', the decision's
 * -- so the line starts a step inside the name it hangs from and its ✗ stands
 * under the row's. Title and ⓘ, like every other row: what it found is a
 * hover away.
 *
 * Its ✗ is not the row's. The row's says the thing is wrong; this one says the
 * rule is, in this case -- which for the dose rule, the commonest reminder
 * there is, is the only way anybody can tell us it fired for nothing.
 */
export function ActiveRuleNotes({
  notes,
  actionsFor,
}: {
  notes: readonly Finding[]
  /** One reminder's "não se aplica". Absent, or nothing, when nothing may be answered. */
  actionsFor?: ((finding: Finding) => ReactNode) | undefined
}) {
  if (notes.length === 0) return null

  return (
    <ul className="flex flex-col py-0.5">
      {notes.map((finding) => (
        <li
          key={finding.fingerprint}
          className="grid grid-cols-[1.5rem_1fr_auto] items-center gap-x-3 px-3 py-[3px]"
        >
          <span aria-hidden="true" />
          <span className="text-tSmall flex min-w-0 items-baseline gap-1.5 pl-4">
            <span aria-hidden="true" className="text-text-weak">
              ↳
            </span>
            <span className="flex shrink-0 self-center">
              <ReasonIcon
                reason={ruleHint(finding, reasonFor(finding))}
                disposition={finding.disposition}
              />
            </span>
            <span className="text-text-strong min-w-0 break-words">
              {ruleTitle(finding) ?? label(CHECK_LABEL, finding.check)}
            </span>
          </span>
          <span className="flex w-[4.5rem] justify-end">{actionsFor?.(finding)}</span>
        </li>
      ))}
    </ul>
  )
}
